import * as React from "react";
import {
  registerAccessTokenGetter,
  registerUnauthorizedHandler,
} from "@/lib/api/client";
import { ApiError } from "@/lib/api/errors";
import { queryClient } from "@/lib/queryClient";
import { getAccessToken, useSession, type AuthTokens } from "./session";
import { decodeAccessToken, msUntilExpiry } from "./token";
import {
  personaFromRoles,
  resolveRoles,
  type Persona,
  type RoleName,
} from "./roles";
import * as authApi from "./authApi";
import type { LoginInput } from "./authApi";

interface AuthContextValue {
  status: "loading" | "authenticated" | "anonymous";
  tenantId: string | null;
  userId: string | null;
  roles: RoleName[];
  persona: Persona;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

// Refresh this many ms before the access token actually expires.
const REFRESH_SKEW_MS = 60_000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { status, claims, refreshToken, setTokens, setAnonymous, clear } =
    useSession();
  const [roles, setRoles] = React.useState<RoleName[]>([]);
  const [rolesHydrated, setRolesHydrated] = React.useState(false);
  const refreshTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const bootstrapRefresh = React.useRef<{
    refreshToken: string;
    promise: Promise<void>;
  } | null>(null);

  const clearAuthenticatedState = React.useCallback(() => {
    queryClient.clear();
    clear();
    setRoles([]);
    setRolesHydrated(false);
  }, [clear]);

  // Wire the API client to this session (token getter, 401 handler).
  React.useEffect(() => {
    registerAccessTokenGetter(getAccessToken);
    registerUnauthorizedHandler(() => {
      // A protected call returned 401; force re-auth.
      clearAuthenticatedState();
    });
  }, [clearAuthenticatedState]);

  const applyTokens = React.useCallback(
    async (tokens: AuthTokens) => {
      setTokens(tokens);
      setRoles(await resolveRoles());
      setRolesHydrated(true);
    },
    [setTokens],
  );

  // Bootstrap: if a refresh token survived a reload, exchange it for an access
  // token; otherwise resolve to anonymous.
  React.useEffect(() => {
    if (status !== "loading") return;
    if (!refreshToken) {
      setAnonymous();
      return;
    }
    if (bootstrapRefresh.current?.refreshToken === refreshToken) return;

    const promise = authApi
      .refresh(refreshToken)
      .then(async (tokens) => {
        const current = useSession.getState();
        if (
          current.status === "loading" &&
          current.refreshToken === refreshToken
        ) {
          await applyTokens(tokens);
        }
      })
      .catch(() => {
        const current = useSession.getState();
        if (
          current.status === "loading" &&
          current.refreshToken === refreshToken
        ) {
          setAnonymous();
        }
      });
    bootstrapRefresh.current = { refreshToken, promise };
  }, [status, refreshToken, applyTokens, setAnonymous]);

  // Silent refresh shortly before access-token expiry.
  React.useEffect(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    if (status !== "authenticated" || !claims || !refreshToken) return;
    const delay = Math.max(0, msUntilExpiry(claims) - REFRESH_SKEW_MS);
    refreshTimer.current = setTimeout(() => {
      authApi
        .refresh(refreshToken)
        .then((tokens) => void applyTokens(tokens))
        .catch(() => clearAuthenticatedState());
    }, delay);
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [status, claims, refreshToken, applyTokens, clearAuthenticatedState]);

  const login = React.useCallback(
    async (input: LoginInput) => {
      const tokens = await authApi.login(input);
      queryClient.clear();
      await applyTokens(tokens);
    },
    [applyTokens],
  );

  const logout = React.useCallback(async () => {
    const token = useSession.getState().refreshToken;
    if (token) {
      await authApi.logout(token).catch((err: unknown) => {
        // Best-effort: ignore anything but unexpected errors.
        if (!(err instanceof ApiError)) throw err;
      });
    }
    clearAuthenticatedState();
  }, [clearAuthenticatedState]);

  const value = React.useMemo<AuthContextValue>(() => {
    const activeClaims = claims ?? (status === "authenticated" && getAccessToken()
      ? decodeAccessToken(getAccessToken()!)
      : null);
    const hydratedStatus =
      status === "authenticated" && !rolesHydrated ? "loading" : status;
    return {
      status: hydratedStatus,
      tenantId: activeClaims?.tenantId ?? null,
      userId: activeClaims?.userId ?? null,
      roles,
      persona: personaFromRoles(roles),
      login,
      logout,
    };
  }, [status, claims, roles, rolesHydrated, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
